'use client';

import React, { useEffect, useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { SlidersHorizontal, Cpu, Zap } from 'lucide-react';
import { motion } from 'framer-motion';

interface ParameterTunerMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; hints?: any[] };
  payload: {
    scenario?: string;
    outputs: Array<{ id: string; label: string; baseValue: number; targetMin: number; targetMax: number }>;
    sliders: Array<{ id: string; label: string; min: number; max: number; step: number; effects: Array<{ outputId: string; multiplier: number }> }>;
    cards: Array<{ id: string; label: string; effects: Array<{ outputId: string; multiplier: number }> }>;
    _hasHints?: boolean;
  };
}

export default function ParameterTunerMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: any) {
  const actualMechanicId = mechanicId || id || '';
  const heading = payload?.scenario || prompt || '';
  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={heading}
      icon={<SlidersHorizontal className="w-5 h-5" />}
      iconLabel="Parameter Tuner — Adjust controls to hit target ranges"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer, hintRevealed) => (
        <ParameterTunerMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} tier={tier} hintRevealed={hintRevealed} />
      )}
    </BaseMechanicShell>
  );
}

function ParameterTunerMechanicInner({ payload, submissionState, setAnswer, tier, hintRevealed }: any) {
  const sliders = payload?.sliders || [];
  const cards = payload?.cards || [];
  const outputs = payload?.outputs || [];

  const [sliderValues, setSliderValues] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    sliders.forEach((p: any) => {
      init[p.id] = p.min || 0;
    });
    return init;
  });

  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  useEffect(() => {
    setAnswer(JSON.stringify({ sliders: sliderValues, cards: activeCardId ? [activeCardId] : [] }));
  }, [sliderValues, activeCardId, setAnswer]);

  const currentOutputs = outputs.map((out: any) => {
    let val = out.baseValue || 0;
    
    sliders.forEach((s: any) => {
      const sVal = sliderValues[s.id] ?? s.min;
      const effectObj = s.effects?.find((e: any) => e.outputId === out.id);
      const effect = effectObj ? effectObj.multiplier : 0;
      val += (sVal * effect);
    });
    
    if (activeCardId) {
      const c = cards.find((c: any) => c.id === activeCardId);
      if (c) {
        const effectObj = c.effects?.find((e: any) => e.outputId === out.id);
        val += (effectObj ? effectObj.multiplier : 0);
      }
    }
    
    return { ...out, currentVal: val };
  });

  return (
    <div className="flex flex-col gap-6 font-sans">
      {/* OUTPUTS (Gauges) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {currentOutputs.map((out: any) => {
          const isGreen = out.currentVal >= out.targetMin && out.currentVal <= out.targetMax;
          const isUnder = out.currentVal < out.targetMin;
          const isOver = out.currentVal > out.targetMax;
          const showGreenZone = tier === 'Easy'
            || (tier === 'Medium' && hintRevealed)
            || tier === 'Hard' ? false : true; // Hard never shows green zone bar
          const showTargetText = tier !== 'Hard'; // Hard hides target range text too
          
          return (
            <div key={out.id} className={`p-4 rounded-xl border-2 transition-colors duration-300 ${isGreen && showGreenZone ? 'bg-green-500/10 border-green-500/50' : 'bg-card border-border/50'}`}>
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-sm tracking-wide uppercase text-muted-foreground">{out.label}</span>
                <span className={`font-mono font-bold text-lg ${isGreen && showGreenZone ? 'text-green-500' : 'text-foreground'}`}>
                  {out.currentVal.toFixed(1)}
                </span>
              </div>
              
              {showTargetText && (
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Target: {out.targetMin} - {out.targetMax}</span>
                </div>
              )}
              
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden relative">
                {/* Target Zone Indicator — hidden on Hard, shown on Easy always, on Medium after hint */}
                {tier !== 'Hard' && (tier === 'Easy' || hintRevealed) && (
                  <div 
                    className="absolute h-full bg-green-500/20" 
                    style={{ 
                      left: `${Math.max(0, Math.min(100, (out.targetMin / (out.targetMax * 1.5)) * 100))}%`, 
                      width: `${Math.min(100, ((out.targetMax - out.targetMin) / (out.targetMax * 1.5)) * 100)}%` 
                    }} 
                  />
                )}
                
                {/* Current Value Bar */}
                <motion.div 
                  className={`h-full ${isGreen && showGreenZone ? 'bg-green-500' : 'bg-primary'}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, Math.max(0, (out.currentVal / (out.targetMax * 1.5)) * 100))}%` }}
                  transition={{ type: 'spring', stiffness: 100 }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SLIDERS */}
        <div className="flex flex-col gap-4 p-5 bg-muted/20 rounded-2xl border border-border/50">
          <h4 className="text-xs font-bold uppercase tracking-widest text-primary/70 flex items-center gap-2 mb-2">
            <SlidersHorizontal className="w-4 h-4" /> System Inputs
          </h4>
          {sliders.map((param: any) => (
            <div key={param.id} className="flex flex-col gap-2">
              <div className="flex justify-between items-center text-sm">
                <span className="font-medium text-foreground/90">{param.label}</span>
                <span className="font-mono bg-background px-2 py-0.5 rounded border border-border text-primary">{sliderValues[param.id]}</span>
              </div>
              <input
                type="range"
                min={param.min || 0}
                max={param.max || 100}
                step={param.step || 1}
                disabled={submissionState !== 'idle' && submissionState !== 'error'}
                value={sliderValues[param.id]}
                onChange={(e) => setSliderValues({ ...sliderValues, [param.id]: Number(e.target.value) })}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-[10px] uppercase font-bold text-muted-foreground/60">
                <span>Min: {param.min || 0}</span>
                <span>Max: {param.max || 100}</span>
              </div>
            </div>
          ))}
        </div>

        {/* CARDS */}
        <div className="flex flex-col gap-4 p-5 bg-muted/20 rounded-2xl border border-border/50">
          <h4 className="text-xs font-bold uppercase tracking-widest text-primary/70 flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4" /> Modifiers (Select 1)
          </h4>
          <div className="flex flex-col gap-3">
            {cards.map((card: any) => {
              const isActive = activeCardId === card.id;
              return (
                <button
                  type="button"
                  key={card.id}
                  disabled={submissionState !== 'idle' && submissionState !== 'error'}
                  onClick={() => setActiveCardId(isActive ? null : card.id)}
                  className={`
                    p-3 rounded-xl border-2 text-left transition-all duration-200 flex items-center justify-between
                    ${isActive ? 'bg-primary/10 border-primary shadow-sm' : 'bg-card border-border/60 hover:border-primary/40'}
                  `}
                >
                  <span className={`font-medium ${isActive ? 'text-primary' : 'text-foreground/80'}`}>{card.label}</span>
                  <div className="flex gap-2">
                    {(card.effects || []).map((eff: any, i: number) => (
                      <span key={i} className={`text-xs font-mono px-1.5 py-0.5 rounded ${eff.multiplier > 0 ? 'bg-green-500/10 text-green-600' : 'bg-destructive/10 text-destructive'}`}>
                        {eff.multiplier > 0 ? '+' : ''}{eff.multiplier}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
            {cards.length === 0 && (
              <div className="text-sm text-muted-foreground italic text-center p-4">No modifiers available.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
